import {
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalRoadmapSortingKeys,
  GoalRoadmapSortingOptions,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
} from '@griply/types';

import { sortGoals } from './sort-goals';

export type BuildSortedGoalTreeOptions<
  TGoal extends Goal = Goal,
  TLifeArea extends LifeArea = LifeArea,
> = {
  goals: TGoal[];
  sortedLifeAreas: TLifeArea[];
  goalGroupBy?: GoalGroupBy;
  goalSortingMode?: GoalSortingMode;
  goalRoadmapSorting?: Partial<Record<GoalRoadmapSortingOptions, ID[]>>;
  goalGroupSorting?: {
    lifeArea?: {
      sorting: Record<ID, ID[]>;
    };
  };
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

export const buildSortedGoalTree = <
  TGoal extends Goal,
  TLifeArea extends LifeArea,
>({
  goals,
  sortedLifeAreas,
  goalGroupBy,
  goalSortingMode,
  goalRoadmapSorting,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
}: BuildSortedGoalTreeOptions<TGoal, TLifeArea>): TGoal[] => {
  if (!goals.length) {
    return [];
  }

  const rootGoals = goals.filter((goal) => !goal.parentIds?.length);

  const customOrder =
    goalRoadmapSorting?.[GoalRoadmapSortingKeys.InProgress] ?? [];

  const validLifeAreaIds = new Set(sortedLifeAreas.map((la) => la.id));
  const lifeAreaOrder = new Map(
    sortedLifeAreas.map((la, index) => [la.id, index]),
  );

  const sortMode = goalSortingMode ?? GoalSortingMode.StartDate;

  const sortedRootGoals =
    goalGroupBy === GroupByMode.LifeArea
      ? [
          ...sortedLifeAreas
            .map((lifeArea) =>
              sortGoals(
                rootGoals.filter(
                  ({ lifeAreaId }) => lifeAreaId === lifeArea.id,
                ),
                {
                  sortMode,
                  allGoals: goals,
                  customOrder: lifeArea.goalSorting ?? [],
                  taskCounts,
                  goalProgressCounts,
                },
              ),
            )
            .flat(),
          ...sortGoals(
            rootGoals.filter(
              ({ lifeAreaId }) =>
                !lifeAreaId || !validLifeAreaIds.has(lifeAreaId),
            ),
            {
              allGoals: goals,
              sortMode,
              customOrder: goalGroupSorting?.lifeArea?.sorting?.['null'] ?? [],
              taskCounts,
              goalProgressCounts,
            },
          ),
        ]
      : sortGoals(rootGoals, {
          allGoals: goals,
          sortMode,
          customOrder,
          lifeAreaOrder,
          taskCounts,
          goalProgressCounts,
        });

  const processGoals = (parentGoals: TGoal[]): TGoal[] =>
    parentGoals.flatMap((parentGoal) => {
      const childGoals = goals.filter(
        (goal) => goal.parentIds?.[goal.parentIds.length - 1] === parentGoal.id,
      );

      const sortedChildGoals = sortGoals(childGoals, {
        sortMode,
        customOrder: parentGoal.goalSorting ?? [],
        allGoals: goals,
        taskCounts,
        goalProgressCounts,
      });

      return [parentGoal, ...processGoals(sortedChildGoals)];
    });

  return processGoals(sortedRootGoals);
};
